"use strict";

/**
 * Last-touch campaign attribution helper.
 *
 * Reads utm_source + utm_id and optional utm_* fields from a URL search string,
 * plus Reddit click IDs used for CAPI matching, sanitizes them, and persists
 * them in localStorage. Every valid campaign page-load overwrites whatever was
 * stored previously (last-touch model).
 *
 * UTM attribution requires utm_source + utm_id. Reddit click ID attribution can
 * stand alone. The stored value is a plain JSON-serialisable object; nothing
 * sensitive is captured. Full landing URLs are intentionally not stored.
 *
 * Usage:
 *   import { captureFromSearch, getStoredAttribution, clearStoredAttribution }
 *     from './campaignAttribution';
 *
 *   // On every page-load in App.js:
 *   captureFromSearch(location.search);
 *
 *   // Just before signing in:
 *   const acq = getStoredAttribution();
 */

export const ATTRIBUTION_STORAGE_KEY = "ouro_acq";
export const REDDIT_CLICK_ID_STORAGE_KEY = "ouro_reddit_click_id";

/** Maximum character length for any single campaign field. */
const MAX_FIELD_LEN = 128;
const MAX_REDDIT_CLICK_ID_LEN = 256;
const REDDIT_CLICK_ID_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const REDDIT_CLICK_ID_PARAMS = ["rdt_cid", "reddit_click_id"];

/** Allow alphanumeric, hyphens, underscores, dots – standard UTM-safe chars. */
const SAFE_PATTERN = /^[A-Za-z0-9._\-]+$/;

/**
 * Trim, truncate, and enforce the safe-character allowlist.
 * Returns null if the value is absent or fails validation.
 */
function sanitizeField(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().slice(0, MAX_FIELD_LEN);
  if (!trimmed) return null;
  if (!SAFE_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function sanitizeRedditClickId(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().slice(0, MAX_REDDIT_CLICK_ID_LEN);
  if (!trimmed) return null;
  if (!SAFE_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function getFirstSafeParam(params, names) {
  for (const name of names) {
    const value = sanitizeRedditClickId(params.get(name));
    if (value) return value;
  }
  return null;
}

function normalizeAttributionShape(value) {
  if (!value || typeof value !== "object") return null;

  const source = sanitizeField(value.source);
  const id = sanitizeField(value.id);
  const redditClickId = sanitizeRedditClickId(value.redditClickId);

  if ((!source || !id) && !redditClickId) return null;

  return {
    source: source || null,
    id: id || null,
    medium: sanitizeField(value.medium),
    name: sanitizeField(value.name),
    content: sanitizeField(value.content),
    term: sanitizeField(value.term),
    redditClickId,
  };
}

function storeRedditClickId(redditClickId) {
  if (!redditClickId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      REDDIT_CLICK_ID_STORAGE_KEY,
      JSON.stringify({ value: redditClickId, capturedAt: Date.now() })
    );
  } catch (_) {
    // Best-effort only – attribution should never block page rendering.
  }
}

export function getStoredRedditClickId() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(REDDIT_CLICK_ID_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = sanitizeRedditClickId(parsed?.value);
    const capturedAt = Number(parsed?.capturedAt);
    if (!value || !Number.isFinite(capturedAt)) return null;
    if (Date.now() - capturedAt > REDDIT_CLICK_ID_TTL_MS) {
      window.localStorage.removeItem(REDDIT_CLICK_ID_STORAGE_KEY);
      return null;
    }
    return value;
  } catch (_) {
    return null;
  }
}

/**
 * Parse and validate campaign params from a URLSearchParams (or search string).
 * Returns null unless there is either a valid UTM pair or a valid Reddit click ID.
 */
export function parseAttribution(search) {
  try {
    const params =
      typeof search === "string"
        ? new URLSearchParams(search)
        : search;

    const source = sanitizeField(params.get("utm_source"));
    const id = sanitizeField(params.get("utm_id"));
    const redditClickId = getFirstSafeParam(params, REDDIT_CLICK_ID_PARAMS);

    // UTMs require source + id; Reddit click ID can stand alone for CAPI matching.
    if ((!source || !id) && !redditClickId) return null;

    return {
      source: source || null,
      id: id || null,
      medium: sanitizeField(params.get("utm_medium")),
      name: sanitizeField(params.get("utm_campaign")),
      content: sanitizeField(params.get("utm_content")),
      term: sanitizeField(params.get("utm_term")),
      redditClickId,
    };
  } catch (_) {
    return null;
  }
}

/**
 * If the search string contains valid attribution, overwrite localStorage with it.
 * No-ops when running server-side (SSR) or when the campaign is invalid.
 * Returns the parsed attribution object, or null if nothing was stored.
 */
export function captureFromSearch(search) {
  if (typeof window === "undefined") return null;

  const attribution = parseAttribution(search);
  if (!attribution) return null;

  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution)
    );
  } catch (_) {
    // Best-effort only – private browsing or quota may block writes.
  }
  storeRedditClickId(attribution.redditClickId);

  return attribution;
}

/**
 * Return the current stored attribution payload, or null if none exists.
 */
export function getStoredAttribution() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    const parsed = raw ? normalizeAttributionShape(JSON.parse(raw)) : null;
    const redditClickId = getStoredRedditClickId();
    if (parsed) {
      return {
        ...parsed,
        redditClickId: parsed.redditClickId || redditClickId,
      };
    }
    if (!redditClickId) return null;
    return normalizeAttributionShape({ redditClickId });
  } catch (_) {
    return null;
  }
}

export function appendAttributionQueryParams(targetUrl, attribution) {
  const acq = normalizeAttributionShape(attribution);
  if (!targetUrl || !acq) return targetUrl;

  const parts = [];
  if (acq.source && acq.id) {
    parts.push(["acq_source", acq.source]);
    parts.push(["acq_id", acq.id]);
    if (acq.medium) parts.push(["acq_medium", acq.medium]);
    if (acq.name) parts.push(["acq_name", acq.name]);
    if (acq.content) parts.push(["acq_content", acq.content]);
    if (acq.term) parts.push(["acq_term", acq.term]);
  }
  if (acq.redditClickId) {
    parts.push(["acq_reddit_click_id", acq.redditClickId]);
  }
  if (!parts.length) return targetUrl;

  const separator = targetUrl.includes("?") ? "&" : "?";
  return `${targetUrl}${separator}${parts
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&")}`;
}

/**
 * Remove stored attribution. Call after it has been sent to the server so it
 * is not re-sent on subsequent (returning-user) logins.
 */
export function clearStoredAttribution() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch (_) {
    // Best-effort only.
  }
}
